<template>
    <search-form-layout
        class="activity-search-form"
        :popup="popup"
        title="activities.activities"
        @close-popup="$emit('close-popup')"
    >
        <template v-slot:form>
            <v-form ref="form" v-model="_formValid">
                <v-row>
                    <v-col v-if="!searchWithoutDestination" md="4" cols="12">
                        <city-country-autocomplete
                            v-if="$conf.public.countrySearchActivities"
                            ref="locationAutocomplete"
                            v-model="cityCountryModel"
                            filled
                            :rules="[v => !!v || `${$t('destination')} ${$t('validation.required')}`]"
                            :default-cities="popularDestinations"
                            product-type="ACTIVITY"
                        />
                        <city-autocomplete
                            v-else
                            ref="locationAutocomplete"
                            v-model="city"
                            filled
                            :rules="[v => !!v || `${$t('destination')} ${$t('validation.required')}`]"
                            :default-cities="popularDestinations"
                            product-type="ACTIVITY"
                        />
                    </v-col>
                    <v-col :md="searchWithoutDestination ? 6 : 4" cols="12">
                        <activity-search-datepicker
                            :start-date="startDate"
                            :end-date="endDate"
                            @change-start="startDate = $event"
                            @change-end="endDate = $event"
                        />
                    </v-col>
                    <v-col :md="searchWithoutDestination ? 4 : 2" cols="12">
                        <tourists-select-menu
                            :_adults="adults"
                            :_children-ages="childrenAges"
                            token="guest"
                            :max-total="50"
                            @change-adults="adults = $event"
                            @change-children-ages="childrenAges = $event"
                        />
                    </v-col>
                    <v-col md="2" cols="12" class="hidden-sm-and-down">
                        <v-btn
                            class="activity-search-form__submit"
                            block
                            size="x-large"
                            color="primary"
                            :disabled="!summaryValid"
                            @click="search"
                        >
                            {{ $t('search') }}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-form>
            <additional-options-wrapper
                ref="addForm"
                :suppliers="suppliers"
                :options="[
                    'activity-type',
                    'promocode',
                    'citizenship',
                    'internalSuppliers',
                    'externalSuppliers',
                    'supplierId',
                    ...(hasDuration ? ['duration'] : []),
                ]"
            >
                <template v-slot:header>
                    <div class="d-flex align-center">
                        <v-expansion-panel-title
                            :class="{'text-caption': $breakpoint.smAndDown}"
                            class="pl-1 width-auto"
                        >
                            {{ $t('extra_options') }}
                        </v-expansion-panel-title>
                        <v-btn
                            v-if="isShowOwnActivitiesCalendar"
                            :disabled="!city"
                            :icon="$breakpoint.smAndDown"
                            variant="text"
                            dense
                            class="ml-auto text-capitalize font-weight-regular view-calendar-btn"
                            @click="isShowActivityCalendar = true"
                        >
                            <template v-if="$breakpoint.smAndDown">
                                <v-icon>mdi-calendar</v-icon>
                            </template>
                            <template v-else>{{ $t('view_calendar') }}</template>
                        </v-btn>
                        <activity-calendar-modal
                            v-model="isShowActivityCalendar"
                            :city="city"
                            :query-params="{
                                adults,
                                childrenAges,
                            }"
                            :first-start-date="startDate"
                        />
                    </div>
                </template>
                <template v-slot:customOptions>
                    <custom-additional-options ref="additionalOptions" product-name="excursion" />
                </template>
            </additional-options-wrapper>
            <v-row class="hidden-md-and-up">
                <v-col md="2" cols="12">
                    <v-btn block size="x-large" color="primary" :disabled="!summaryValid" @click="search">
                        {{ $t('search') }}
                    </v-btn>
                </v-col>
            </v-row>
        </template>
    </search-form-layout>
</template>

<script lang="ts">
    import {Component, mixins, Prop} from 'vue-facing-decorator'
    import CityAutocomplete from '~/components/search/forms/CityAutocomplete.vue'
    import SearchFormLayout from '~/components/parts/SearchFormLayout.vue'
    import TouristsSelectMenu from '~src/components/search/forms/touristsSelectMenu.src'
    import {AdditionalSearchOptionsMixin} from '@/mixins/AdditionalSearchOptionsMixin.vue'
    import AdditionalOptionsWrapper from '~src/components/snippets/AdditionalSearchOptions/additionalOptionsWrapper.src'
    import {CHANGE_LOCALE_EVENT, CHANGE_TAB_MOBILE_EVENT, EventBus} from '@/utils/event-bus'
    import CustomAdditionalOptions from '~src/components/snippets/AdditionalSearchOptions/CustomAdditionalOptions.vue'
    import ActivitySearchDatepicker from '~src/components/activities/search/forms/ActivitySearchDatepicker.vue'
    import ValidateSearchOptionsMixin from '~src/mixins/ValidateSearchOptionsMixin.vue'
    import CityCountryAutocomplete from '~/components/search/forms/CityCountryAutocomplete.vue'

    @Component({
        components: {
            CityCountryAutocomplete,
            ActivitySearchDatepicker,
            CustomAdditionalOptions,
            ActivityCalendarModal: defineAsyncComponent(
                () => import('@/components/activities/modals/ActivityCalendarModal.vue')
            ),
            TouristsSelectMenu,
            CityAutocomplete,
            SearchFormLayout,
            AdditionalOptionsWrapper,
        },
        mixins: [ValidateSearchOptionsMixin],
    })
    export class ActivitySearchForm extends mixins(AdditionalSearchOptionsMixin) {
        @Prop({default: false}) popup
        @Prop() cityId

        city = null
        country = null
        startDate = null
        endDate = null
        adults = 2
        childrenAges = []
        popularDestinations = []
        isShowActivityCalendar = false
        suppliers = []

        beforeUnmount() {
            EventBus.$off(CHANGE_LOCALE_EVENT, this.reload)
        }

        async reload() {
            await this.loadPopularDestinations()
            this.$refs.locationAutocomplete.clear()
            if (this.city && this.city.id) {
                try {
                    const {
                        cities: [city],
                    } = await this.$api.locations.get({id: this.city.id, limitCities: 1})
                    this.city = city
                    // eslint-disable-next-line no-empty
                } catch (e) {}
            } else if (this.country && this.country.id) {
                try {
                    const {
                        countries: [country],
                    } = await this.$api.locations.get({id: this.country.id, limitCities: 1})
                    this.country = country
                    // eslint-disable-next-line no-empty
                } catch (e) {}
            } else {
                this.$refs.locationAutocomplete.load()
            }
        }

        newQuery() {
            const query = {
                ...(this.city?.id && {cityId: this.city.id}),
                ...(this.country?.id && !this.city?.id && {countryId: this.country.id}),
                startDate: this.startDate,
                endDate: this.endDate,
                adults: this.adults,
                childrenAges: this.childrenAges,
            }

            if (persistentStore.selectedCompanyAndAgent) {
                query.clientId = persistentStore.selectedCompanyAndAgent.company.id

                if (persistentStore.selectedCompanyAndAgent.agent) {
                    query.agentId = persistentStore.selectedCompanyAndAgent.agent.personId
                }
            }

            return query
        }

        async search() {
            const query = this.newQuery()
            if (this.city?.id) {
                activitiesRuntimeStore.SET_CITY(this.city)
            } else if (this.country?.id) {
                activitiesRuntimeStore.SET_COUNTRY(this.country)
            } else {
                activitiesRuntimeStore.SET_COUNTRY({})
            }
            this.$emit('close-popup')
            if (this.$route.name !== 'activities') {
                activitiesRuntimeStore.newSearch()
            }
            try {
                await this.$router.push({name: 'activities', query})
            } catch (e) {
                await activitiesRuntimeStore.search(this.searchRequest)
            }
            EventBus.$emit(CHANGE_TAB_MOBILE_EVENT, 1)
        }

        async mounted() {
            EventBus.$on(CHANGE_LOCALE_EVENT, this.reload)
            await this.$store.restored
            await this.loadPopularDestinations()
            if (this.cityId || this.searchRequest.cityId) {
                await this.loadCity()
            } else if (this.searchRequest.countryId) {
                await this.loadCountry()
            }
            if (this.$dateFns.isAfter(this.$dateFns.parseISO(this.searchRequest.startDate), new Date())) {
                this.startDate = this.searchRequest.startDate
                this.endDate = this.searchRequest.endDate
            } else {
                this.startDate = this.$dateFns.format(this.$dateFns.addDays(new Date(), 1))
                this.endDate = this.$dateFns.format(this.$dateFns.addDays(new Date(), 7))
            }
            //TODO Need refactoring with other products
            this.adults = authStore.user.isEmployeeOrExternalCorp ? 1 : this.searchRequest.adults
            this.childrenAges = authStore.user.isEmployeeOrExternalCorp ? [] : [...this.searchRequest.childrenAges]
            if (this.isTO1) {
                try {
                    this.suppliers = (await this.$api.suppliers.get({productType: 'excursion'}))?.suppliers
                    // eslint-disable-next-line no-empty
                } catch (e) {}
            }
        }

        async loadCity() {
            const cityId = this.cityId || this.searchRequest.cityId
            if (cityId && activitiesRuntimeStore.city.id !== cityId) {
                await activitiesRuntimeStore.loadCity(cityId)
            }
            this.city = cityId && Object.keys(activitiesRuntimeStore.city).length ? activitiesRuntimeStore.city : null
        }

        async loadCountry() {
            const countryId = this.searchRequest.countryId
            await activitiesRuntimeStore.loadCountry(countryId)
            this.country = activitiesRuntimeStore.country
        }

        async loadPopularDestinations() {
            try {
                this.popularDestinations = (
                    await this.$api.locations.get({limitCities: 7, productType: 'ACTIVITY'})
                ).cities
                // eslint-disable-next-line no-empty
            } catch (e) {}
        }

        get cityCountryModel() {
            if (this.country) {
                return this.country
            }

            return this.city
        }

        set cityCountryModel(location) {
            if (!location) {
                this.city = null
                this.country = null
            }

            if (location?.type === 'country') {
                this.city = null
                this.country = location
            } else {
                this.country = null
                this.city = location
            }
        }

        get isTO1() {
            return authStore.isTO1
        }

        get isShowOwnActivitiesCalendar() {
            return this.$conf.public.searchFormOptions?.ownActivitiesCalendar
        }

        get searchRequest() {
            return activitiesStore.searchRequest
        }

        get searchWithoutDestination() {
            return false
        }

        get hasDuration() {
            return this.$conf.public.activities.durationSearchParam
        }
    }

    export default ActivitySearchForm
</script>

<style scoped lang="scss">
    .view-calendar-btn {
        font-size: 0.9375rem;
        letter-spacing: normal;
    }
</style>
